if (!customElements.get('multi-variant-picker')) {
  customElements.define(
    'multi-variant-picker',
    class MultiVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.moneyFormat = this.dataset.moneyFormat;
        this.productId = Number(this.dataset.productId);
        this.formId = this.dataset.formId;
        this.inputs = Array.from(this.querySelectorAll('input[type="checkbox"][data-variant-id]'));
        this.totalEl = this.querySelector('[data-multi-total]');
        this.gramsEl = this.querySelector('[data-multi-total-grams]');
        this.priceEl = this.querySelector('[data-multi-total-price]');
        this.committed = new Set(
          this.inputs
            .filter((input) => input.dataset.committed === 'true')
            .map((input) => Number(input.dataset.variantId))
        );
      }

      connectedCallback() {
        this.onChange = this.onChange.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);

        this.addEventListener('change', this.onChange);
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);

        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      onChange() {
        this.render();
      }

      onCartUpdate(payload) {
        if (payload && payload.source === 'multi-variant-picker' && payload.productId === this.productId) {
          this.commitSelection();
          return;
        }
        this.refreshFromCart();
      }

      async refreshFromCart() {
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        const liveIds = new Set(
          cart.items.filter((item) => item.product_id === this.productId).map((item) => item.variant_id)
        );
        this.committed = liveIds;
        this.inputs.forEach((input) => {
          const isCommitted = liveIds.has(Number(input.dataset.variantId));
          input.toggleAttribute('data-committed', isCommitted);
          input.checked = isCommitted;
        });
        this.render();
      }

      getSelectedIds() {
        return this.inputs.filter((input) => input.checked).map((input) => Number(input.dataset.variantId));
      }

      getAdditions() {
        return this.inputs
          .filter((input) => input.checked && !this.committed.has(Number(input.dataset.variantId)))
          .map((input) => ({ id: Number(input.dataset.variantId), quantity: 1 }));
      }

      getRemovals() {
        const selected = new Set(this.getSelectedIds());
        return Array.from(this.committed).filter((id) => !selected.has(id));
      }

      hasChanges() {
        return this.getAdditions().length > 0 || this.getRemovals().length > 0;
      }

      commitSelection() {
        this.committed = new Set(this.getSelectedIds());
        this.inputs.forEach((input) => {
          input.toggleAttribute('data-committed', this.committed.has(Number(input.dataset.variantId)));
        });
        this.render();
      }

      render() {
        const selected = this.inputs.filter((input) => input.checked);
        const additions = this.getAdditions();
        const removals = this.getRemovals();

        let totalGrams = 0;
        let totalPriceCents = 0;
        selected.forEach((input) => {
          totalGrams += this.toGrams(Number(input.dataset.variantGrams), input.dataset.variantWeightUnit);
          totalPriceCents += Number(input.dataset.variantPrice);
        });

        this.inputs.forEach((input) => {
          const label = input.nextElementSibling;
          if (!label) return;
          const inCommitted = this.committed.has(Number(input.dataset.variantId));
          if (input.checked && inCommitted) label.dataset.state = 'clean';
          else if (input.checked) label.dataset.state = 'pending-add';
          else if (inCommitted) label.dataset.state = 'pending-remove';
          else label.dataset.state = '';
        });

        const showTotal = selected.length > 0 || removals.length > 0;
        this.totalEl.toggleAttribute('hidden', !showTotal);
        if (showTotal) {
          this.gramsEl.textContent = this.formatGrams(totalGrams);
          this.priceEl.textContent = this.formatMoney(totalPriceCents);
        }

        this.updateSubmitButton(selected.length, additions.length, removals.length);
      }

      updateSubmitButton(selectedCount, addCount, removeCount) {
        const form = document.getElementById(this.formId);
        const button = form.querySelector('[type="submit"]');
        const textEl = button.querySelector('span');
        const strings = window.variantStrings;

        let label;
        let disabled = false;

        if (addCount === 0 && removeCount === 0) {
          label = selectedCount === 0 ? strings.multiPickerEmpty : strings.multiPickerInCart;
          disabled = true;
        } else if (addCount > 0 && removeCount === 0) {
          label = addCount === 1
            ? strings.multiPickerAddOne
            : strings.multiPickerAddMany.replace('[count]', addCount);
        } else if (removeCount > 0 && addCount === 0) {
          label = removeCount === 1
            ? strings.multiPickerRemoveOne
            : strings.multiPickerRemoveMany.replace('[count]', removeCount);
        } else {
          label = strings.multiPickerUpdate;
        }

        textEl.textContent = label;
        button.toggleAttribute('disabled', disabled);
        button.toggleAttribute('aria-disabled', disabled);
      }

      toGrams(value, unit) {
        switch ((unit || 'g').toLowerCase()) {
          case 'kg':
            return value * 1000;
          case 'lb':
            return value * 453.59237;
          case 'oz':
            return value * 28.349523125;
          default:
            return value;
        }
      }

      formatGrams(grams) {
        const rounded = Math.round(grams);
        const withThousands = String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return `${withThousands} g`;
      }

      formatMoney(cents) {
        const amount = cents / 100;
        const withThousands = (str) => str.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const withDecimals = withThousands(amount.toFixed(2).replace('.', ','));
        const noDecimals = withThousands(String(Math.round(amount)));
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, withDecimals)
          .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withDecimals);
      }
    }
  );
}
